import "react-accessible-accordion/dist/fancy-example.css";

import {
    Accordion,
    AccordionItem,
    AccordionItemButton,
    AccordionItemHeading,
    AccordionItemPanel,
} from "react-accessible-accordion";
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import {
    getSurveyFreeTextResponses,
    getSurveyResponses,
} from "../../services/survey/survey.service";
import { useContext, useEffect, useState } from "react";

import { DataTableComponent } from "../common/TableComponent/DataTableComponent";
import IconButton from "../common/IconButton/IconButton";
import Loader from "../common/Loader/Loader";
import { SurveyContext } from "../../utils/SurveyContext";
import WordCloud from "react-d3-cloud";
import { scaleOrdinal } from "d3-scale";
import { schemeCategory10 } from "d3-scale-chromatic";
import { toast } from "react-toastify";
import useGetSurveyResponses from "../../utils/useGetSurveyResponses";
import { t } from "i18next";
import DownloadIcon from "../../assets/Download.svg";

ChartJS.register(
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
);

const getBarComponent = (question) => {
    return (
        <Bar
            data={{
                labels: question.options.map((o) => o.value),
                datasets: [
                    {
                        data: question.responses.map((response) => response),
                        backgroundColor: [
                            "#999999",
                            "#000000",
                            "#E60000",
                            "#666666",
                            "#FF9800",
                        ],
                        maxBarThickness: 60,
                    },
                ],
            }}
            options={{
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        display: false,
                    },
                    datalabels: {
                        color: "#FFFFFF",
                    },
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            precision: 0,
                            stepSize: 1,
                            callback: (value) => Math.floor(value),
                        },
                    },
                },
            }}
            height={"100%"}
            width={"100%"}
        />
    );
};

const getPieComponent = (question) => {
    return (
        <Pie
            data={{
                labels: question.options.map((o) => o.value),
                datasets: [
                    {
                        data: question.responses.map((response) => response),
                        backgroundColor: [
                            "#E60000",
                            "#666666",
                            "#FFCE56",
                            "#8BC34A",
                            "#FF9800",
                        ],
                    },
                ],
            }}
            options={{
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                    },
                    datalabels: {
                        color: "#FFFFFF",
                    },
                },
            }}
            height={"100%"}
            width={"100%"}
        />
    );
};
const getWordCloudComponent = (question) => {
    const words = question.options.map((item) => ({
        text: item.optionText,
        value: item.responseCount,
    }));

    // Create tooltip div once
    let tooltipDiv = document.getElementById("wordcloud-tooltip");
    if (!tooltipDiv) {
        tooltipDiv = document.createElement("div");
        tooltipDiv.id = "wordcloud-tooltip";
        Object.assign(tooltipDiv.style, {
            position: "fixed",
            padding: "6px 10px",
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            color: "#fff",
            borderRadius: "4px",
            fontSize: "12px",
            pointerEvents: "none",
            zIndex: "1000",
            display: "none",
        });
        document.body.appendChild(tooltipDiv);
    }

    const handleMouseOver = (event, word) => {
        tooltipDiv.style.left = `${event.clientX + 10}px`;
        tooltipDiv.style.top = `${event.clientY + 10}px`;
        tooltipDiv.innerText = `${word.text}: ${word.value}`;
        tooltipDiv.style.display = "block";
    };

    const handleMouseOut = () => {
        tooltipDiv.style.display = "none";
    };

    const schemeCategory10ScaleOrdinal = scaleOrdinal(schemeCategory10);

    return (
        <WordCloud
            data={words}
            width={500}
            height={300}
            fontWeight="bold"
            fontSize={(word) => Math.max(10, Math.log2(word.value + 1) * 10)}
            spiral="rectangular"
            rotate={(word) => word.value % 360}
            padding={2}
            random={Math.random}
            fill={(d, i) => schemeCategory10ScaleOrdinal(i)}
            onWordMouseOver={handleMouseOver}
            onWordMouseOut={handleMouseOut}
        />
    );
};

const getFreeTextComponent = (question) => {
    const columns = [
        {
            name: t("Responses"),
            field: "reponses",
            width: "300px",
        },
        {
            name: t("NoOfResponses"),
            field: "numOfResponses",
            width: "300px",
        },
    ];
    let responses = [];
    if (!question.responses || question.responses.length === 0) {
        return <div>{t("noResponsesAvailable")}</div>;
    } else {
        const uniqueResponses = Array.from(new Set(question.responses));
        // Count occurrences of each unique response
        const counts = question.responses.reduce((acc, resp) => {
            acc[resp] = (acc[resp] || 0) + 1;
            return acc;
        }, {});
        responses = uniqueResponses.map((resp, index) => ({
            reponses: resp,
            id: index + 1,
            numOfResponses: counts[resp],
        }));
    }
    return (
        <div
            style={{
                width: "100%",
                paddingRight: "1rem",
                overflowX: "auto",
                height: "11.25rem",
                borderRadius: "0.5rem",
            }}
        >
            <DataTableComponent
                columns={columns}
                data={responses}
                tableName="FreeText Responses"
                pagination={false}
            />
        </div>
    );
};

const ChartSection = ({ openModal, data }) => (
    <div style={{ maxWidth: "75vw", margin: "0 auto" }}>
        <Accordion
            allowMultipleExpanded
            allowZeroExpanded
            preExpanded={["0"]}
            style={{ border: "none", boxShadow: "none" }}
        >
            {data?.map((section, index) => (
                <AccordionItem
                    key={index}
                    uuid={index.toString()}
                    expanded={index === 0}
                    style={{ border: "none", boxShadow: "none" }}
                >
                    <AccordionItemHeading>
                        <AccordionItemButton
                            style={{
                                marginBottom: "0.5rem",
                                background:
                                    "linear-gradient(56.16deg,rgb(225, 225, 225) -9.11%, #FFFFFF 47.66%)",
                                padding: "1.25rem",
                                borderRadius: "0.5rem",
                                fontWeight: "bold",
                                fontSize: "1.2rem",
                                border: "none",
                                outline: "none",
                                cursor: "pointer",
                            }}
                        >
                            {section?.sectionTitle
                                ? `${t("Section")} ${index + 1}: ${section.sectionTitle}`
                                : t("untitled")}
                        </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel>
                        <div className="row">
                            {section.questions.map((question, qIdx) => (
                                <div key={qIdx} className="col-md-12">
                                    <h4
                                        style={{
                                            textAlign: "left",
                                            fontSize: "1.1rem",
                                            marginBottom: "0.625rem",
                                            maxWidth: "100%",
                                        }}
                                    >
                                        {question.isMandatory && (
                                            <span className="survey-required">
                                                *
                                            </span>
                                        )}
                                        {qIdx + 1}. {question.questionText}
                                    </h4>
                                    <div
                                        style={{
                                            height:
                                                (question.code === "OneWord" || question.questionTypeId === 6) ||
                                                (question.code === "FreeText" || question.questionTypeId === 3)
                                                    ? "fit-content"
                                                    : "11.25rem",
                                            position: "relative",
                                            marginBottom: "0.625rem",
                                            display: "flex",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                // position: "absolute",
                                                top: 0,
                                                left: 0,
                                                background:
                                                    "linear-gradient(56.16deg, #FFE9E9 -9.11%, #FFFFFF 47.66%)",
                                                padding: "1rem",
                                                borderRadius: "0.5rem",
                                            }}
                                        >
                                            {(question.code === "SCQ" || question.questionTypeId === 5) ||
                                            (question.code === "MCQ" || question.questionTypeId === 4) ||
                                            question.code === "Rating"
                                                ? getBarComponent(question)
                                                : (question.code === "YesNo" || question.questionTypeId === 1) ||
                                                    (question.code === "TrueFalse" || question.questionTypeId === 2)
                                                  ? getPieComponent(question)
                                                  : (question.code === "OneWord" || question.questionTypeId === 6)
                                                    ? getWordCloudComponent(
                                                          question
                                                      )
                                                    : getFreeTextComponent(
                                                          question
                                                      )}
                                        </div>
                                    </div>
                                    {(question.questionTypeId !== 3 && question.questionTypeId !== 6)
                                         && (
                                            <div
                                                style={{
                                                    width: "100%",
                                                    display: "flex",
                                                    justifyContent: "flex-end",
                                                    paddingTop: "2rem",
                                                }}
                                            >
                                                <IconButton
                                                    text={t("moreDetails")}
                                                    onClick={() =>
                                                        openModal(question)
                                                    }
                                                    style={{
                                                        backgroundColor:
                                                            "#F0F0F0",
                                                        color: "#454647",
                                                        border: "1px solid #B3B2B4",
                                                    }}
                                                />
                                            </div>
                                        )}
                                </div>
                            ))}
                        </div>
                    </AccordionItemPanel>
                </AccordionItem>
            ))}
        </Accordion>
    </div>
);

const SurveyDashboard = ({ surveyId, languageMasterId, handleSurveyDataDownload, rowSurveyData }) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalQuestion, setModalQuestion] = useState(null);
    const { setSurveyTab } = useContext(SurveyContext);

    const [
        surveyResponses,
        surveyFreeTextResponses,
        surveyNumberOfResponses,
        loading,
    ] = useGetSurveyResponses(surveyId, languageMasterId);

    useEffect(() => {
        if (!loading && surveyNumberOfResponses === 0) {
            setSurveyTab("surveyManagement");
            toast.error(t("responseNotReceivedYet"));
        }
    }, [surveyNumberOfResponses, loading]);

    let combined = [];
    if (surveyResponses && surveyResponses.questions?.length > 0) {
        combined.push(
            ...surveyResponses.questions.map((q) => ({
                questionId: q.questionId,
                questionText: q.questionText,
                sectionTitle: q.sectionTitle,
                allOptions: q.options,
                code: q.code,
                isMandatory: q.isMandatory,
                userAnswers: null,
                questionTypeId: q.questionTypeId,
            }))
        );
    }
    if (surveyFreeTextResponses?.length > 0) {
        combined.push(
            ...(surveyFreeTextResponses[0]?.userResponses || []).map((r) => ({
                questionId: r.questionId,
                questionText: r.questionText,
                sectionTitle: r.sectionTitle === "Unknown Section Title" ? "" : r.sectionTitle,
                // sectionTitle: r.sectionTitle,
                allOptions: null,
                code: "FreeText",
                isMandatory: r.isMandatory,
                userAnswers: r.userAnswers,
                questionTypeId: 3
            }))
        );
    }

    combined.sort((a, b) => a.questionId - b.questionId);

    function countResponses(options) {
        const counts = {};
        options.forEach((opt) => {
            if (!(opt.optionText in counts)) {
                counts[opt.optionText] = 0;
            }
        });
        options.forEach((opt) => {
            if (opt.isSelected) {
                counts[opt.optionText] += opt.responseCount;
            }
        });
        const uniqueOrder = [...new Set(options.map((opt) => opt.optionText))];
        const result = uniqueOrder.map((opt) => counts[opt]);

        return result;
    }

    const sectionMap = {};

    combined.forEach((q) => {
        const section = q.sectionTitle || "";
        if (!sectionMap[section]) sectionMap[section] = [];
        let options;
        if (q.code !== "OneWord" || q.questionTypeId !== 6) {
            options = q.allOptions
                ? Array.from(
                      new Map(
                          q.allOptions
                              .filter((opt) => opt?.optionText)
                              .map((opt) => [
                                  opt.optionText.trim().toLowerCase(),
                                  {
                                      id: opt.optionText
                                          .toLowerCase()
                                          .replace(/\s+/g, "_"),
                                      value: opt.optionText,
                                      isSelected: opt.isSelected || false,
                                      optionText: opt.optionText,
                                      responseCount: opt.responseCount,
                                  },
                              ])
                      ).values()
                  )
                : undefined;
        } else {
            options = [...q.allOptions];
        }

        const responses = q.allOptions
            ? countResponses(q.allOptions)
            : q.userAnswers || undefined;

        sectionMap[section].push({
            questionId: q.questionId,
            questionText: q.questionText,
            questionTypeId: q.questionTypeId,
            uniqueNumber: 0,
            code: q.code,
            isMandatory: q.isMandatory,
            options,
            responses,
        });
    });
    const data = Object.entries(sectionMap).map(
        ([sectionTitle, questions]) => ({
            sectionTitle,
            questions: Array.isArray(questions)
                ? questions.map((q, idx) => ({
                      ...q,
                      uniqueNumber: idx + 1,
                  }))
                : [],
        })
    );
    const openModal = (item) => {
        setModalQuestion(item);
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        setModalQuestion(null);
    };
    if (loading) {
        return <Loader />;
    }

    return (
            <>
                <div
                    style={{
                        height: "73vh",
                        overflowY: "auto",
                        overflowX: "hidden",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0 }}>
                            {t("surveyTitle")}:{" "}
                            {surveyResponses?.surveyTitle && surveyResponses.surveyTitle}
                        </h3>
                        <button className="org-download-btn" onClick={() => handleSurveyDataDownload(rowSurveyData)} style={{marginRight: '5px'}}>
                            {t("downloadSurveyData")}
                            <img
                                src={DownloadIcon}
                                alt="Download"
                                style={{ width: "16px", height: "18px" }}
                            />
                        </button>
                    </div>
                        
                    <div
                        style={{
                            marginTop: "0.5vw",
                            marginBottom: "2vw",
                            fontSize: "clamp(0.9rem, 1vw, 1.1rem)",
                            color: "#888",
                            fontStyle: "italic",
                        }}
                    >
                        {t('totalNumberofRespondents')}: {surveyNumberOfResponses || 0}
                    </div>
                    {data && <ChartSection openModal={openModal} data={data} />}
                    {modalOpen && modalQuestion && (
                        <div
                            style={{
                                position: "fixed",
                                top: 0,
                                left: 0,
                                width: "100vw",
                                height: "100vh",
                                background: "rgba(0,0,0,0.3)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                zIndex: 1000,
                                padding: "2vw",
                            }}
                        >
                            <div
                                style={{
                                    background: "#fff",
                                    borderRadius: "2vw",
                                    padding: "4vw 3vw 3vw 3vw",
                                    minWidth: "min(350px, 80vw)",
                                    minHeight: "min(300px, 60vw)",
                                    width: "100%",
                                    maxWidth: "500px",
                                    position: "relative",
                                    boxShadow: "0 2px 16px rgba(0,0,0,0.2)",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    justifyContent: "flex-start",
                                }}
                            >
                                <button
                                    onClick={closeModal}
                                    style={{
                                        position: "absolute",
                                        top: "1vw",
                                        right: "1vw",
                                        background: "transparent",
                                        border: "none",
                                        fontSize: "clamp(1.2rem, 2vw, 2rem)",
                                        cursor: "pointer",
                                    }}
                                    aria-label="Close"
                                >
                                    ×
                                </button>

                                <div
                                    style={{
                                        marginBottom: "2vw",
                                        wordBreak: "break-word",
                                        fontWeight: "bold",
                                        fontSize: "clamp(1rem, 1.2vw, 1.3rem)",
                                        textAlign: "left",
                                        padding: "0.5vw 0.5vw",
                                        borderRadius: "0.5vw",
                                        background:
                                            "linear-gradient(56.16deg,rgb(193, 190, 190) -9.11%, #FFFFFF 47.66%)",
                                    }}
                                >
                                    {modalQuestion.isMandatory && (
                                        <span className="survey-required">* </span>
                                    )}
                                    {modalQuestion.questionText}
                                </div>
                                <div
                                    style={{
                                        marginTop: "0.5vw",
                                        marginBottom: "0.5vw",
                                        fontSize: "clamp(0.9rem, 1vw, 1.1rem)",
                                        color: "#888",
                                        fontStyle: "italic",
                                    }}
                                >
                                    {t("questionType")}: {modalQuestion.code}
                                </div>
                                {modalQuestion.options && (
                                    <div style={{ width: "100%", maxWidth: "350px" }}>
                                        {(modalQuestion.code === "SCQ" || modalQuestion.questionTypeId === 5) && (
                                            <div className="ques-options-container">
                                                <div
                                                    className="ques-options-scq"
                                                    style={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: "1vw",
                                                        alignItems: "flex-start",
                                                    }}
                                                >
                                                    {modalQuestion.options.map(
                                                        (option, i) => (
                                                            <label
                                                                text={i}
                                                                className="ques-option-label"
                                                                style={{
                                                                    fontSize:
                                                                        "clamp(0.95rem, 1vw, 1.1rem)",
                                                                    marginRight: "1vw",
                                                                    display: "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: "0.5vw",
                                                                }}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    disabled
                                                                    name={`scq-${modalQuestion.questionId}`}
                                                                    value={option.id}
                                                                    style={{
                                                                        accentColor:
                                                                            "#B1000E",
                                                                        width: "1.2em",
                                                                        height: "1.2em",
                                                                    }}
                                                                />
                                                                {option.value} -{" "}
                                                                {
                                                                    modalQuestion
                                                                        .responses[i]
                                                                }
                                                                {t("responses")}
                                                            </label>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {(modalQuestion.code === "MCQ" || modalQuestion.questionTypeId === 4) && (
                                            <div className="ques-options-container">
                                                <div
                                                    className="ques-options-mcq"
                                                    style={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: "1vw",
                                                        alignItems: "flex-start",
                                                    }}
                                                >
                                                    {modalQuestion.options.map(
                                                        (option, i) => (
                                                            <label
                                                                text={i}
                                                                className="ques-option-label"
                                                                style={{
                                                                    fontSize:
                                                                        "clamp(0.95rem, 1vw, 1.1rem)",
                                                                    marginRight: "1vw",
                                                                    display: "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: "0.5vw",
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    disabled
                                                                    name={`mcq-${modalQuestion.questionId}`}
                                                                    value={option.id}
                                                                    style={{
                                                                        accentColor:
                                                                            "#B1000E",
                                                                        width: "1.2em",
                                                                        height: "1.2em",
                                                                    }}
                                                                />
                                                                {option.value} -{" "}
                                                                {
                                                                    modalQuestion
                                                                        .responses[i]
                                                                }
                                                                {t("responses")}
                                                            </label>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Yes/No Options */}
                                        {((modalQuestion?.code === "YesNo" || modalQuestion?.questionTypeId === 1) ||
                                            (modalQuestion?.code === "TrueFalse" || modalQuestion?.questionTypeId === 2)) && (
                                            <div className="ques-options-container">
                                                <div
                                                    className="ques-yes-no-options"
                                                    style={{
                                                        display: "flex",
                                                        flexWrap: "wrap",
                                                        gap: "1vw",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {modalQuestion.options.map(
                                                        (option, i) => (
                                                            <label
                                                                text={i}
                                                                className="ques-yes-no-label"
                                                                style={{
                                                                    fontSize:
                                                                        "clamp(0.95rem, 1vw, 1.1rem)",
                                                                    marginRight: "1vw",
                                                                    display: "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    gap: "0.5vw",
                                                                }}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    disabled
                                                                    name={`question-${modalQuestion.questionId}`}
                                                                    value={option.id}
                                                                    style={{
                                                                        accentColor:
                                                                            "#B1000E",
                                                                        width: "1.2em",
                                                                        height: "1.2em",
                                                                    }}
                                                                />
                                                                {option.value} -{" "}
                                                                {
                                                                    modalQuestion
                                                                        .responses[i]
                                                                }
                                                                {t("responses")}
                                                            </label>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ textAlign: "right", marginTop: "0.5rem", borderTop: "1px solid #ccc", paddingTop: "0.5rem" }}>
                    <button className="cancelButton" onClick={() => setSurveyTab("surveyManagement")}>
                        {t("close")}
                    </button>
                </div>
            </>
    );
};

export default SurveyDashboard;
